/**
 * Estados de carga, error y vacio.
 *
 * Estan centralizados porque son la parte que mas se copia mal: una pantalla
 * que muestra "cargando" para siempre cuando en realidad hubo un 403 es peor
 * que una que no carga nada.
 */

import type { ReactNode } from "react";

import { ApiError, NetworkError } from "../api/client";

export function Spinner({ label = "Cargando..." }: { readonly label?: string }) {
  return (
    <p className="feedback feedback--loading" role="status" aria-live="polite">
      {label}
    </p>
  );
}

export function EmptyState({ children }: { readonly children: ReactNode }) {
  return <p className="feedback feedback--empty">{children}</p>;
}

/**
 * Traducir un error a algo que un operador pueda accionar.
 *
 * La distincion clave es 401 vs 403: la primera es "tu sesion", la segunda es
 * "tus permisos sobre esta tienda". Mostrar el mismo cartel para las dos manda
 * a la gente a desloguearse y volver a entrar por un problema que el logout no
 * arregla.
 */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isUnauthenticated) {
      return "La sesion vencio o el token no es valido. Volve a iniciar sesion.";
    }
    if (error.isForbidden) {
      return "Tu usuario no tiene acceso a esta tienda.";
    }
    if (error.isAuthNotConfigured) {
      return (
        "El backend no tiene configurada la autenticacion (SUPABASE_URL / " +
        "SUPABASE_JWKS). Es un problema del despliegue, no de tu usuario."
      );
    }
    if (error.status === 404) {
      return "El recurso no existe.";
    }
    return error.detail ?? `La API respondio ${error.status}.`;
  }

  if (error instanceof NetworkError) {
    return (
      "No se pudo contactar la API. Verificar que este levantada y que " +
      "CORS_ORIGINS incluya el origen de este panel."
    );
  }

  if (error instanceof Error) return error.message;
  return "Error desconocido.";
}

export function ErrorState({
  error,
  onRetry,
}: {
  readonly error: unknown;
  readonly onRetry?: (() => void) | undefined;
}) {
  return (
    <div className="feedback feedback--error" role="alert">
      <p>{describeError(error)}</p>
      {onRetry ? (
        <button type="button" onClick={onRetry}>
          Reintentar
        </button>
      ) : null}
    </div>
  );
}

/**
 * "Sin permiso": el usuario esta logueado, pero el servidor le nego ESTE
 * recurso (403).
 *
 * Es un estado propio y no un error generico porque la accion que corresponde
 * es distinta: un 401 se arregla volviendo a entrar, un 403 solo se arregla si
 * alguien le concede el acceso. Mostrar "reintentar" en un 403 invita a
 * golpear la puerta cerrada.
 */
export function ForbiddenState({
  detail,
}: {
  readonly detail?: string | undefined;
}) {
  return (
    <div className="feedback feedback--error" role="alert">
      <p>
        <strong>Sin permiso.</strong> Tu usuario no tiene acceso a este recurso.
      </p>
      <p>{detail ?? "Si deberia tenerlo, pediselo a un administrador."}</p>
    </div>
  );
}

/**
 * "El usuario no tiene NINGUNA tienda concedida".
 *
 * Distinto de un 403 puntual: aca no hay nada que mostrar en todo el panel, asi
 * que el layout ademas esconde el menu. El texto nombra la tabla que hay que
 * tocar porque el operador que ve esto no puede hacer nada con un "sin acceso"
 * a secas.
 */
export function NoStoreAccessState() {
  return (
    <div className="feedback feedback--error" role="alert">
      <p>
        <strong>Tu usuario no tiene ninguna tienda concedida.</strong>
      </p>
      <p>
        El panel entero esta acotado por tienda, asi que sin al menos una fila en{" "}
        <code>user_store_access</code> no hay nada que mostrar. Un administrador
        tiene que darte acceso.
      </p>
    </div>
  );
}
